/**
 * Dinheiro no painel: sempre centavos inteiros (bigint no banco, number seguro no JSON do PostgREST).
 * Nada de ponto flutuante para somar: some centavos, formate só na borda.
 */

const brl = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });
const plain = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const compact = new Intl.NumberFormat("pt-BR", { notation: "compact", maximumFractionDigits: 1 });

export type CentsLike = number | bigint | string | null | undefined;

export function toCents(value: CentsLike): number | null {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "bigint") return Number(value);
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? Math.round(n) : null;
}

/** 123456 → "R$ 1.234,56" (com espaço rígido, como o Intl). Nulo → "—" não: devolve fallback. */
export function formatMoney(value: CentsLike, fallback = ""): string {
  const c = toCents(value);
  if (c === null) return fallback;
  return brl.format(c / 100);
}

const brlRound = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0, minimumFractionDigits: 0 });
/** Valores redondos sem centavos ("R$ 2.500"); com centavos, igual a formatMoney. Para faixas e resumos. */
export function formatMoneyShort(value: CentsLike, fallback = ""): string {
  const c = toCents(value);
  if (c === null) return fallback;
  return c % 100 === 0 ? brlRound.format(c / 100) : brl.format(c / 100);
}

/** 123456 → "1.234,56" (sem símbolo), para campos de edição. */
export function formatMoneyPlain(value: CentsLike): string {
  const c = toCents(value);
  if (c === null) return "";
  return plain.format(c / 100);
}

/** 123456789 → "R$ 1,2 mi" para eixos de gráfico. */
export function formatMoneyCompact(value: CentsLike): string {
  const c = toCents(value);
  if (c === null) return "";
  const reais = c / 100;
  if (Math.abs(reais) < 10_000) return brl.format(Math.round(reais));
  return `R$ ${compact.format(reais)}`;
}

/** Valor para CSV (Excel em pt-BR): "1234,56", sem separador de milhar. */
export function formatMoneyCsv(value: CentsLike): string {
  const c = toCents(value);
  if (c === null) return "";
  const neg = c < 0;
  const abs = Math.abs(c);
  const s = `${Math.trunc(abs / 100)},${String(abs % 100).padStart(2, "0")}`;
  return neg ? `-${s}` : s;
}

/**
 * Converte o texto digitado em centavos, sem ponto flutuante.
 * Aceita "1.234,56", "1234,56", "1234.56", "R$ 1.234", "1234,5", "-10,00".
 * Vírgula é sempre decimal. Sem vírgula, um único ponto seguido de 1 ou 2 dígitos é decimal;
 * nos outros casos o ponto é separador de milhar. Mais de 2 casas: arredonda meio para cima.
 */
export function parseMoney(text: string): number | null {
  let s = text.replace(/R\$/gi, "").replace(/[\s ]/g, "");
  if (s === "") return null;
  let negative = false;
  if (/^[-−]/.test(s)) {
    negative = true;
    s = s.slice(1);
  }
  if (!/^[0-9.,]+$/.test(s)) return null;
  let intPart: string;
  let fracPart = "";
  if (s.includes(",")) {
    const parts = s.split(",");
    if (parts.length !== 2) return null;
    intPart = parts[0]!.replace(/\./g, "");
    fracPart = parts[1]!;
    if (fracPart.includes(".")) return null;
  } else {
    const dots = s.split(".");
    if (dots.length === 2 && dots[1]!.length > 0 && dots[1]!.length <= 2) {
      intPart = dots[0]!;
      fracPart = dots[1]!;
    } else {
      if (dots.slice(1).some((g) => g.length !== 3)) return null;
      intPart = dots.join("");
    }
  }
  if (intPart === "") intPart = "0";
  if (!/^\d+$/.test(intPart) || !/^\d*$/.test(fracPart)) return null;
  let cents = Number(intPart) * 100 + Number((fracPart + "00").slice(0, 2));
  if (fracPart.length > 2 && Number(fracPart[2]) >= 5) cents += 1;
  if (!Number.isSafeInteger(cents)) return null;
  return negative ? -cents : cents;
}

/** Soma segura de centavos (ignora nulos). */
export function sumCents(values: CentsLike[]): number {
  let total = 0;
  for (const v of values) total += toCents(v) ?? 0;
  return total;
}

const pct = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 1, minimumFractionDigits: 0 });
/** 12.345 → "12,3%". Recebe o percentual já em pontos (0 a 100). */
export function formatPct(value: number | string | null | undefined, fallback = ""): string {
  if (value === null || value === undefined || value === "") return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? `${pct.format(n)}%` : fallback;
}

const int = new Intl.NumberFormat("pt-BR");
export function formatInt(value: number | string | null | undefined, fallback = ""): string {
  if (value === null || value === undefined || value === "") return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? int.format(n) : fallback;
}
